import React from 'react';
import Icon from '../../../components/AppIcon';

const CommercialValuesCard = ({ commercialData }) => {
  if (!commercialData) return null;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 2
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const priceChange = commercialData?.salePrice - commercialData?.listPrice;
  const priceChangePercent = ((priceChange / commercialData?.listPrice) * 100)?.toFixed(1);

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation">
      <div className="flex items-center space-x-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-lg">
          <Icon name="DollarSign" size={20} color="white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Valores Comerciales</h3>
          <p className="text-sm text-muted-foreground">Precios en Pesos Mexicanos (MXN)</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* List Price */}
        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <Icon name="Tag" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Precio de Lista</span>
            </div>
            <Icon name="Info" size={14} className="text-muted-foreground" />
          </div>
          <div className="text-2xl font-bold text-foreground mb-1">
            {formatCurrency(commercialData?.listPrice)}
          </div>
          <div className="text-xs text-muted-foreground">
            MSRP • Precio sugerido por el fabricante
          </div>
        </div>

        {/* Sale Price */}
        <div className="p-4 bg-muted rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <Icon name="ShoppingCart" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Precio de Venta</span>
            </div>
            <div className={`flex items-center space-x-1 text-xs px-2 py-1 rounded-full ${
              priceChange < 0 
                ? 'bg-success/10 text-success' 
                : priceChange > 0 
                  ? 'bg-warning/10 text-warning' :'bg-muted-foreground/10 text-muted-foreground'
            }`}>
              <Icon 
                name={priceChange < 0 ? "TrendingDown" : priceChange > 0 ? "TrendingUp" : "Minus"} 
                size={12} 
              />
              <span>{Math.abs(priceChangePercent)}%</span>
            </div>
          </div>
          <div className="text-2xl font-bold text-foreground mb-1">
            {formatCurrency(commercialData?.salePrice)}
          </div>
          <div className="text-xs text-muted-foreground">
            Precio promedio de mercado
          </div>
        </div>
      </div>
      {/* Price Difference */}
      <div className="mt-4 p-4 bg-background border border-border rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Icon name="Calculator" size={16} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Diferencia de Precio</span>
          </div>
          <div className={`text-lg font-semibold ${
            priceChange < 0 ? 'text-success' : priceChange > 0 ? 'text-warning' : 'text-muted-foreground'
          }`}>
            {priceChange < 0 ? '-' : priceChange > 0 ? '+' : ''}{formatCurrency(Math.abs(priceChange))}
          </div>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          {priceChange < 0 
            ? 'Descuento respecto al precio de lista'
            : priceChange > 0 
              ? 'Sobreprecio respecto al precio de lista' :'Sin diferencia respecto al precio de lista'
          }
        </div>
      </div>
      {/* Additional Information */}
      <div className="mt-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <span className="text-sm text-muted-foreground">Moneda:</span>
            <div className="flex items-center space-x-1">
              <span className="text-sm font-medium text-foreground">{commercialData?.currency}</span>
              <Icon name="Banknote" size={14} className="text-muted-foreground" />
            </div>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <span className="text-sm text-muted-foreground">Fecha de Actualización:</span>
            <div className="flex items-center space-x-1">
              <span className="text-sm font-medium text-foreground">
                {formatDate(commercialData?.lastUpdated)}
              </span>
              <Icon name="Calendar" size={14} className="text-muted-foreground" />
            </div>
          </div>
        </div>

        <div className="p-3 bg-muted rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Database" size={14} className="text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Fuente de Datos</span>
          </div>
          <div className="text-sm text-muted-foreground">
            {commercialData?.source} • Actualizado cada 24 horas
          </div>
          <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="MapPin" size={12} />
              <span>México</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={12} />
              <span>Zona Centro</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Shield" size={12} />
              <span>Verificado</span>
            </div>
          </div>
        </div>
      </div>
      {/* Disclaimer */}
      <div className="mt-4 p-3 bg-warning/5 border border-warning/20 rounded-lg">
        <div className="flex items-start space-x-2">
          <Icon name="AlertTriangle" size={16} className="text-warning flex-shrink-0 mt-0.5" />
          <div className="text-xs text-muted-foreground">
            <strong className="text-foreground">Nota:</strong> Los precios mostrados son referenciales y pueden variar según la región, concesionario y condiciones de mercado. Consulte con su distribuidor autorizado para obtener precios exactos y disponibilidad.
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommercialValuesCard;